import { Chip, ContentCard } from "@narmi/design_system";
import { Fragment, useEffect, useMemo } from "react";
import { useLocalization } from "@fluent/react";
import { useLocalStorage } from "usehooks-ts";
import { useFeature } from "byzantine/src/dbbl/businessLogic/modules/features/hooks";
import { useIsBusinessUser } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import {
  featureEquals,
  type Features,
  type FeatureValue,
} from "byzantine/src/Feature";
import {
  dismissNewBadgeState,
  type NewBadgeState,
  markNewBadgeSeen,
  shouldShowNewBadge,
} from "byzantine";
import { createFNVHashSync } from "../authentication/utils";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import styles from "./TransferTilesMenu.module.scss";
import PageLayout from "../../src/components/PageLayout";
import LongTransferTile from "./LongTransferTile";
import {
  openSsoOption,
  useMoneyMovementMethods,
} from "../../src/modules/transfers";
import PaymentTile from "./PaymentTile";
import OtherItemTile from "./OtherItemTile";
import AppAuthorize from "../AppAuthorize";
import TransferPill from "./TransferPill";
import { areTemplatesAvailable } from "../Templates/constants";
import { useAccountsContext } from "../contexts/AccountContext";

const TRANSFER_FEATURE_TYPES = [
  "ach_payments",
  "wires",
  "bulk_payments",
  "file_import",
  "bill_pay",
  "loan",
];

const OTHER_FEATURE_TYPES = ["p2p", "zelle", "naf", "sso"];

const INSTANT_FEATURES_VIA_FEDNOW = ["bulk_payments"];
const INVOICE_PAY_NEW_BADGE_STORAGE_KEY = "invoicePayBadgeStateV2";

const TransferTilesMenu = () => {
  const { l10n } = useLocalization();
  const { featureEnabled, features } = useFeature();
  const isBusinessUser = useIsBusinessUser();
  const { currentUser } = useCurrentUser();
  const { externalAccounts } = useAccountsContext();

  const isPersonalUser = !isBusinessUser;

  const moneyMovementFeaturesData = useMoneyMovementMethods({
    isTilesPage: true,
  });

  if (moneyMovementFeaturesData.length === 0) {
    return null;
  }

  const fundsFeature = moneyMovementFeaturesData.find(
    (feature) => feature?.type === "funds"
  );

  const transferFeatures = TRANSFER_FEATURE_TYPES.flatMap((featureType) =>
    moneyMovementFeaturesData.filter((feature) => feature?.type === featureType)
  );

  const otherFeatures = moneyMovementFeaturesData.filter(
    (feature) =>
      typeof feature?.type === "string" &&
      OTHER_FEATURE_TYPES.includes(feature.type)
  );
  const paymentActivityEnabled = featureEnabled("payment_activity");
  const hasDualApprovalPermission = featureEnabled(
    "indigo.DualApprovalRequest_permission"
  );
  const hasRecipientPermission =
    featureEnabled("indigo.Recipient_permission") &&
    !!(
      featureEquals(
        features as unknown as Features,
        "p2p",
        "m2m" as FeatureValue
      ) ||
      features.bill_pay ||
      features.wires
    );
  const templatesEnabled = areTemplatesAvailable(features);

  const hasLinkedExternalAccounts = externalAccounts.length > 0;

  // hacky way to tell if at least 2 of these options are available
  const shouldShowPillSection =
    Number(paymentActivityEnabled) +
      Number(hasDualApprovalPermission) +
      Number(hasRecipientPermission) +
      Number(templatesEnabled) +
      Number(hasLinkedExternalAccounts && isPersonalUser) >=
    2;

  const fednowEnabled = Boolean(
    features?.fednow_send && features?.expedited_payments
  );
  const achPaymentFednowEnabled = Boolean(
    features?.fednow_send && features?.single_expedite
  );
  const invoicePayEnabled = featureEnabled("invoice_pay");
  const invoicePayStorageKey = useMemo(() => {
    const userId = currentUser?.id;

    if (!userId) {
      return `${INVOICE_PAY_NEW_BADGE_STORAGE_KEY}-anonymous`;
    }

    return `${INVOICE_PAY_NEW_BADGE_STORAGE_KEY}-${createFNVHashSync(userId)}`;
  }, [currentUser?.id]);
  const [invoicePayBadgeState, setInvoicePayBadgeState] =
    useLocalStorage<NewBadgeState | null>(invoicePayStorageKey, null);

  useEffect(() => {
    if (!invoicePayEnabled || !currentUser?.id) {
      return;
    }

    setInvoicePayBadgeState((currentState) => markNewBadgeSeen(currentState));
  }, [currentUser?.id, invoicePayEnabled, setInvoicePayBadgeState]);

  const showInvoicePayBadge =
    invoicePayEnabled &&
    !!currentUser?.id &&
    shouldShowNewBadge(invoicePayBadgeState);
  const invoicePayBadge = showInvoicePayBadge && (
    <Chip
      kind="success"
      startIcon="sparkle"
      label={l10n.getString("label-new")}
    />
  );

  return (
    <PageLayout maxWidth="m">
      <div style={{ gap: 0 }}>
        <h1>{l10n.getString("transfer-tiles-page-header")}</h1>
        <p className={styles.subheader}>
          {l10n.getString("transfer-tiles-menu-subheader")}
        </p>
      </div>
      {shouldShowPillSection && (
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexDirection: "row",
            flexWrap: "nowrap",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            overflowX: "auto",
            marginTop: "20px",
          }}
        >
          {paymentActivityEnabled && (
            <TransferPill
              label={l10n.getString("transfer-tiles-payment-activity")}
              redirect="/payment_activity"
            ></TransferPill>
          )}
          {hasDualApprovalPermission && (
            <TransferPill
              label={l10n.getString("transfer-tiles-pending-approvals")}
              redirect="/approvals/pending"
            ></TransferPill>
          )}
          {hasRecipientPermission && (
            <TransferPill
              label={l10n.getString("transfer-tiles-recipients")}
              redirect="/settings/recipients"
            ></TransferPill>
          )}
          {templatesEnabled && (
            <TransferPill
              label={l10n.getString("transfer-tiles-manage-templates")}
              redirect="/templates"
            ></TransferPill>
          )}
          {hasLinkedExternalAccounts && isPersonalUser && (
            <TransferPill
              label={l10n.getString("transfer-tiles-linked-accounts")}
              redirect="/settings/linked_accounts"
            ></TransferPill>
          )}
        </div>
      )}
      {Boolean(fundsFeature) && (
        <div>
          <div className={styles.dividerRow}>
            <p>{l10n.getString("transfer-tiles-menu-me-to-me-label")}</p>
            <div className={styles.dividerLine} />
          </div>
          <LongTransferTile
            redirect={fundsFeature?.data?.route || ""}
            icon="narmi-icon-transfer-arrows"
            header={l10n.getString(fundsFeature?.data?.translationTitle || "")}
            subHeader={l10n.getString(
              fundsFeature?.data?.translationDescription || ""
            )}
            showInstantBadge={fednowEnabled}
          />
        </div>
      )}
      {(invoicePayEnabled || transferFeatures.length > 0) && (
        <div>
          <div className={styles.dividerRow}>
            <p>{l10n.getString("transfer-tiles-payments-label")}</p>
            <div className={styles.dividerLine} />
          </div>
          {invoicePayEnabled && (
            <LongTransferTile
              redirect="/transfer/invoice"
              icon="narmi-icon-file"
              header={l10n.getString("invoice-pay-title")}
              subHeader={l10n.getString("invoice-pay-description")}
              showInstantBadge={Boolean(features?.fednow_send)}
              badge={invoicePayBadge}
              onClick={() => {
                setInvoicePayBadgeState((currentState) =>
                  dismissNewBadgeState(currentState)
                );
              }}
            />
          )}
          <div className={styles.tileGrid}>
            {transferFeatures.map((feature) => (
              <PaymentTile
                key={feature?.type}
                redirect={feature?.data?.route || ""}
                icon={`narmi-icon-${feature?.data?.icon}`}
                header={l10n.getString(feature.data?.translationTitle || "")}
                subHeader={l10n.getString(
                  feature.data?.translationDescription || ""
                )}
                showInstantBadge={
                  (feature?.type === "ach_payments" &&
                    achPaymentFednowEnabled) ||
                  ((INSTANT_FEATURES_VIA_FEDNOW.includes(feature?.type) ||
                    (feature?.type === "file_import" &&
                      featureEnabled("ach_file_import"))) &&
                    fednowEnabled)
                }
              />
            ))}
          </div>
        </div>
      )}
      {otherFeatures.length > 0 && (
        <div>
          <div className={styles.dividerRow}>
            <p>{l10n.getString("transfer-tiles-other-label")}</p>
            <div className={styles.dividerLine} />
          </div>
          <ContentCard kind="bordered" paddingSize="none" radiusSize="m">
            {otherFeatures.map((feature, idx) => (
              <Fragment
                key={
                  feature?.type === "naf"
                    ? `naf-${feature?.nafData?.client_id || idx}`
                    : feature?.ssoData?.url || feature?.type || "other"
                }
              >
                {feature?.ssoData && (
                  <OtherItemTile
                    redirect={feature.ssoData.url}
                    icon={`narmi-icon-${feature.ssoData.icon || ""}`}
                    header={feature.ssoData.title}
                    subHeader={feature.ssoData.description || ""}
                    onClick={() =>
                      feature.ssoData && openSsoOption(feature.ssoData)
                    }
                  />
                )}
                {feature?.type === "naf" && (
                  <AppAuthorize
                    app={feature?.nafData}
                    renderButton={(handleClick) => (
                      <OtherItemTile
                        redirect={`/apps/${feature?.nafData?.client_id || ""}`}
                        icon={`narmi-icon-${feature.nafData?.image}`}
                        header={l10n.getString(feature.nafData?.name || "")}
                        subHeader={l10n.getString(
                          feature.nafData?.description || ""
                        )}
                        onClick={handleClick}
                      />
                    )}
                  />
                )}
                {!["naf", "sso"].includes(feature?.type) && (
                  <OtherItemTile
                    redirect={feature?.data?.route || ""}
                    icon={
                      feature?.type === "zelle"
                        ? "narmi-icon-zelle-outline"
                        : `narmi-icon-${feature?.data?.icon}`
                    }
                    header={l10n.getString(
                      feature.data?.translationTitle ||
                        (feature?.type === "zelle" ? "Zelle" : "")
                    )}
                    subHeader={l10n.getString(
                      feature.data?.translationDescription || ""
                    )}
                  />
                )}
                {idx < otherFeatures.length - 1 && (
                  <div
                    className={`${styles.dividerLine} ${styles.dividerLineMargin}`}
                  />
                )}
              </Fragment>
            ))}
          </ContentCard>
        </div>
      )}
    </PageLayout>
  );
};

export default TransferTilesMenu;
